// BVNDrawer.jsx

import { useState, useEffect } from "react";
import {
    Drawer,
    Box,
    IconButton,
    Typography,
    Avatar,
    Stack,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemText,
    Button,
    useTheme,
    Paper,
    InputLabel,
    TextField
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import WalletIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { BadgeCheck } from "lucide-react";
import { pageStyles } from "../../Styles/pageStyles";
import { commonServices } from "../../Services/commonServices";
import { apiHandler, currencyFormatter, ErrLoaderOff, widthData, Number2Decimal } from "@shared/Functions/apiHandler";
import { useDrawerOpenFun } from "../../Context/modelContext";
import FullPageLoader from "@shared/Components/FullPageLoader.jsx";


const benefitItems = [
    { icon: "💳", title: "Instant Payments", subtitle: "Pay for fuel orders directly from your wallet" },
    { icon: "⚡", title: "Quick Refills", subtitle: "Add money anytime via bank transfer or card" },
    { icon: "🎁", title: "Cashback Rewards", subtitle: "Earn points on every wallet transaction" },
    { icon: "🔒", title: "Secure Transactions", subtitle: "Secured by BVN verification" },
];

export default function BvnVerified({ open, onClose }) {
    const theme = useTheme();
    const classes = pageStyles(theme);
    const [amount, setAmount] = useState(0);
    const [walletDetails, setWalletDetails] = useState({});
    const { setBvnDrawer } = useDrawerOpenFun();
    const [loading, setLoading] = useState(false);

    const SimpleText = ({ title, subtitle }) => {
        return (
            <Box sx={classes.flex_center_btn}>
                <Box>{title}</Box>
                <Box>{subtitle}</Box>
            </Box>
        );
    }

    const addAmount = async () => {
        let payload = {
            "amount": amount
        }
        setLoading(true);
        const res = await commonServices.addFund(payload);
        apiHandler(res, classes.successToast, true);
        ErrLoaderOff(res, setLoading);
        if (res?.status === 200) {
            window.location.href = res?.data?.data?.authorizationUrl;
        }
    }

    const getWalletStatus = async () => {
        const res = await commonServices.getWalletStatus();
        apiHandler(res);
        if (res.status) {
            if (res?.data?.data?.isWalletEnabled) {
                setWalletDetails(res?.data?.data);
            }
        }
    }

    useEffect(() => {
        getWalletStatus();
    }, [open]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={(event, reason) => {
                if (reason === "backdropClick") return; // ignore backdrop clicks
                onClose(event, reason); // allow closing for other reasons (like 'escapeKeyDown')
            }}

            slotProps={{
                paper: {
                    sx: {
                        width: widthData,
                        p: 2,
                        bgcolor: "#FFFDF8",
                        boxShadow: 6,
                    },
                },
            }}
        >
            {loading && <FullPageLoader />}
            <Box>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <IconButton onClick={() => { setBvnDrawer(false); onClose() }} size="small" sx={{ mr: 1 }}>
                        <ArrowBackIosNewIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        BVN Verified
                    </Typography>
                </Box>

                {/* Check + Title */}
                <Stack alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Box sx={classes.successIconDiv}>
                        <BadgeCheck
                            size={60}
                            color={"#2ECC71"} />
                    </Box>

                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        BVN Verified Successfully!
                    </Typography>
                    <Typography sx={classes.darkTextMedium}>
                        Your wallet is now activated and ready to use
                    </Typography>
                </Stack>

                {/* Wallet Card */}
                <Card
                    elevation={0}
                    sx={classes.walletCard}
                >
                    <CardContent sx={classes.flex_start_btn}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                <WalletIcon sx={{ color: theme.palette.text.primary }} />
                            </Avatar>
                            <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    Refuelex Wallet
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {currencyFormatter()} {Number2Decimal(walletDetails?.wallet, 2)}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                    Secured by BVN verification
                                </Typography>
                            </Box>
                        </Stack>

                        <Box sx={classes.flex_end}>
                            <Box sx={classes.walletActive}>
                                Active
                            </Box>
                        </Box>
                    </CardContent>
                </Card>


                <Box sx={{ my: 2 }}>

                    <InputLabel sx={classes.darkTextSmall}> Enter Amount </InputLabel>
                    <TextField
                        placeholder="₦0"
                        size="small"
                        fullWidth
                        sx={classes.inputField}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        type="number"
                        onWheel={(e) => e.target.blur()}
                    />

                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={{ borderRadius: 2, mt: 1 }}
                        onClick={addAmount}
                    >
                        +Add Money to the Wallet
                    </Button>
                </Box>

                {/* Benefits list */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Your Wallet Benefits
                </Typography>

                <List disablePadding sx={{ mb: 2 }}>
                    {benefitItems.map((b, i) => (
                        <Box key={i} sx={{ mb: 1 }}>
                            <ListItem sx={classes.paperDiv2}>
                                <ListItemText
                                    primary={<Typography variant="body1" sx={{ fontWeight: 700 }}> {b.icon} {b.title}</Typography>}
                                    secondary={<Typography sx={classes.lightTextSmall}>{b.subtitle}</Typography>}
                                />
                            </ListItem>
                        </Box>
                    ))}
                </List>

                <Divider sx={{ my: 2 }} />

                {/* Wallet limits box */}
                <Paper sx={classes.infoDiv}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Wallet Limits
                    </Typography>

                    <Stack spacing={1}>
                        <SimpleText title="Wallet Limit" subtitle="₦500,000 " />
                        <SimpleText title="Daily Limit" subtitle="₦25,000" />
                        <SimpleText title="Monthly Limit" subtitle="₦500,000" />

                        <Box>
                            These limits comply with CBN regulations for mobile wallets
                        </Box>
                    </Stack>
                </Paper>

                {/* Action buttons */}
                <Stack spacing={1} sx={{ mt: 1 }}>


                    <Button
                        variant="outlined"
                        size="large"
                        fullWidth
                        onClick={() => { setBvnDrawer(false); onClose() }}
                        sx={classes.skipBtn}
                    >
                        Skip for Now
                    </Button>
                </Stack>
            </Box>
        </Drawer>
    );
}
